const ARTICLES_3 = [
  {
    slug: "event-security-staffing-numbers",
    title: "How Many Security Staff Does Your Event Actually Need?",
    description: "There is no legal ratio for event security staffing. How professional planners calculate numbers from risk — and the posts that must be filled regardless.",
    category: "Sector Guides",
    date: "2026-07-20",
    readMins: 6,
    intro: [
      "Ask five people how many security staff your event needs and you will get five ratios: one per hundred, one per fifty, one per gate. The truth is that no lawful fixed ratio exists — and any provider who quotes one before reading your risk assessment is guessing.",
    ],
    sections: [
      {
        h: "Why the ratios are a myth",
        paras: [
          "UK guidance, including the Purple Guide, is explicit that staffing numbers come from risk assessment, not arithmetic. A seated classical concert for 2,000 and a standing drum-and-bass night for 800 present entirely different demands — the smaller event may need the bigger team.",
          "What the law does require is competence: security functions performed by SIA-licensed staff, in numbers adequate to the risk, under a documented plan.",
        ],
      },
      {
        h: "The inputs that drive the number",
        paras: [
          "Professional planners weigh the audience profile and expected behaviour, alcohol availability, event history, performer profile, venue layout and pinch points, ingress and egress rates, the duration and time of day, and the local context — what else is happening nearby that night.",
          "Each input moves the number: high-energy standing crowds need pit and barrier staff; family events need welfare and lost-child provision; late finishes need egress teams held until the site is clear.",
        ],
      },
      {
        h: "Posts that must be filled regardless",
        paras: [
          "Whatever the headcount, certain functions cannot be zero: entry search and ticket control, stage or performance area security, a response capability for incidents, control room staffing if CCTV and radios are in play, and egress cover until the last attendee leaves.",
          "Supervision is the one organisers forget: teams need team leaders, and team leaders need an event security manager with the authority to act.",
        ],
      },
      {
        h: "Building it into the plan",
        paras: [
          "Numbers belong in the event management plan agreed with the venue and, for licensed or large events, the local Safety Advisory Group. Start that conversation months out — SAG lead times are real, and late changes cost money.",
          "Xtreme Protection plans and staffs event security across England, from community festivals to arena shows. Call +44 2046 402840 with your date and we will build the numbers from your risk, not a ratio.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is there a legal minimum of security per attendee?",
        a: "No. No lawful ratio exists in England. Numbers must come from a documented risk assessment, and for licensed events the Safety Advisory Group will expect to see the reasoning.",
      },
      {
        q: "What is the difference between stewards and security?",
        a: "Stewards provide wayfinding, information and crowd support. Security functions — searching, refusing entry, ejecting people, guarding assets — require SIA-licensed staff. Most events need a planned mix of both.",
      },
      {
        q: "How late can we book event security?",
        a: "Small events can sometimes be staffed at a few weeks' notice, but large or licensed events need months — licensing consultations and SAG engagement have their own timelines that security planning must respect.",
      },
    ],
    relatedServices: ["event-security", "door-supervision", "k9-security-dog-handlers"],
    relatedCities: ["manchester", "london", "brighton-and-hove"],
  },
  {
    slug: "alarm-response-what-happens",
    title: "Alarm Response: What Actually Happens When Your Alarm Goes Off at 3am",
    description: "From sensor to response officer in minutes: how professional alarm response works, what happens on site, and why your staff should never attend.",
    category: "Guides",
    date: "2026-07-20",
    readMins: 5,
    intro: [
      "Most business alarms go off when nobody is there — which is precisely the point. But somebody has to attend, and the choice is between a trained response officer with a torch and a procedure, or your operations manager in their pyjamas. Here is how the professional version works, minute by minute.",
    ],
    sections: [
      {
        h: "From sensor to dispatch",
        paras: [
          "When your alarm triggers, the signal reaches the monitoring centre within seconds. Operators filter obvious false activations, then dispatch the nearest response officer — a licensed, vetted professional already on the road in your area, holding your keys in a sealed, tracked system.",
          "Because response units run dedicated patrol areas, attendance is measured in minutes in urban England, and every movement from dispatch onward is GPS-logged.",
        ],
      },
      {
        h: "On site: the response routine",
        paras: [
          "The officer's first job is the outside: perimeter check for signs of entry — forced doors, broken glazing, disturbed fencing. If the building is secure externally, they enter with your keys, silence the alarm and conduct a systematic internal sweep.",
          "Findings dictate the response. A blown-open door is reset, secured and documented. Signs of intrusion trigger a different protocol: withdraw, preserve the scene, call police, and stand by until the building is re-secured — boarding if necessary.",
        ],
      },
      {
        h: "Why staff should never attend",
        paras: [
          "The employer who sends a lone employee to a night activation is carrying a duty-of-care risk with no upside: if it is a break-in, that employee meets it alone and untrained. Insurers and health and safety law both take a dim view of the DIY keyholder after an incident.",
          "There is a quieter cost too: broken sleep, next-day absence and the steady resentment of whoever holds the keys. Outsourced response typically costs less per month than one bad callout costs in goodwill.",
        ],
      },
      {
        h: "The morning report",
        paras: [
          "By the time you open your laptop, the full picture is waiting: activation time, dispatch and arrival times, findings, photographs, actions taken and any follow-up needed. That audit trail is what insurers ask for — and what turns a 3am alarm from a drama into a line item.",
          "Xtreme Protection provides key holding and alarm response across England, with verified attendance on every activation. Call +44 2046 402840 to register your keys.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you charge for false alarms?",
        a: "Pricing structures vary — typically a monthly holding fee plus a per-callout charge, or an inclusive allowance. We quote transparently in writing so you know the cost before the first activation.",
      },
      {
        q: "Will the police attend my alarm?",
        a: "Police response to alarms is limited and usually requires confirmed evidence of a crime in progress. A professional response officer provides exactly that verification — which is why monitored alarm plus response is now the standard expectation.",
      },
      {
        q: "Who holds the keys and how are they protected?",
        a: "Keys are held in a secure control centre under BS 7984 procedures: sealed, coded, tracked on every movement and never identified with your address. You can audit key movements at any time.",
      },
    ],
    relatedServices: ["key-holding-alarm-response", "mobile-security-patrols", "cctv-monitoring"],
    relatedCities: ["leicester", "coventry", "stoke-on-trent"],
  },
  {
    slug: "security-patrols-how-they-work",
    title: "Mobile Security Patrols: Why Randomness Beats Routine",
    description: "Unpredictable patrols deter more crime than predictable ones. How mobile security patrols work, what a visit includes, and how visits are verified.",
    category: "Guides",
    date: "2026-07-21",
    readMins: 5,
    intro: [
      "The most valuable thing a mobile patrol sells is uncertainty. A criminal watching your site can plan around a guard's round or a camera's blind spot — but a marked vehicle that might appear at 11pm, 2am or 4am is a problem with no solution. That is the logic at the heart of mobile security patrols.",
    ],
    sections: [
      {
        h: "The psychology of the unpredictable visit",
        paras: [
          "Opportunist crime is a risk calculation: time on site versus chance of interruption. Random patrol timing attacks that calculation directly — there is no safe window when the next visit could be any minute. Predictability, by contrast, is a gift to anyone watching.",
          "This is why professional patrol providers vary routes and times within agreed windows rather than running the same round nightly, and why the marked vehicle matters: being seen arriving is half the deterrent.",
        ],
      },
      {
        h: "What a patrol visit actually includes",
        paras: [
          "A proper visit is a checklist, not a drive-past: perimeter walk checking fences, gates, doors and windows; signs of forced entry, vandalism, fly-tipping or leaks; external lighting and CCTV checks; and, where contracted, internal patrols, meter reads, plant checks and alarm resets.",
          "Lock and unlock services extend the same model to the working day: an officer opens up before staff arrive and secures the site after they leave, removing lone-working risk at both ends.",
        ],
      },
      {
        h: "Verification: proving the visit happened",
        paras: [
          "The patrol industry runs on proof now. GPS-tracked vehicles log arrival and departure; NFC or QR checkpoint tags around your site confirm the officer walked the route; photographs with timestamps evidence the condition of every check point.",
          "Your report arrives automatically after each visit or each morning. If a provider cannot show you this before you sign, they are asking you to pay for trust.",
        ],
      },
      {
        h: "The patrol stack: combining with response and cameras",
        paras: [
          "Patrols work best stacked: scheduled and random visits for presence, key holding and alarm response for the moments between, and monitored CCTV for continuous oversight. Each layer covers the others' gaps at a fraction of the cost of static guarding.",
          "Xtreme Protection runs mobile patrol routes across England with GPS and NFC verification as standard. Call +44 2046 402840 to add your site to a route.",
        ],
      },
    ],
    faqs: [
      {
        q: "How many patrol visits does a site need per night?",
        a: "Most low-to-medium risk premises take two to four randomly timed visits per night. Higher-risk sites increase frequency or combine patrols with static guarding during peak-risk hours.",
      },
      {
        q: "Are patrol vehicles marked?",
        a: "Yes — marked vehicles are part of the deterrent. Sign-written patrol cars arriving unpredictably make your site visibly protected to anyone watching.",
      },
      {
        q: "Can patrols cover multiple nearby sites?",
        a: "That is exactly the model: sites on shared routes share the cost, which is why patrols deliver professional presence at a fraction of a static officer's price.",
      },
    ],
    relatedServices: ["mobile-security-patrols", "key-holding-alarm-response", "vacant-property-security"],
    relatedCities: ["preston", "chester", "lancaster"],
  },
  {
    slug: "retail-security-guard-rights",
    title: "What Powers Do Retail Security Guards Actually Have?",
    description: "Security guards have no police powers — but the ones they do have matter. Searches, detention and citizen's arrest explained for retailers and the public.",
    category: "Compliance",
    date: "2026-07-21",
    readMins: 6,
    intro: [
      "Can a security guard search your bag? Detain a shoplifter? Ban you from a store? The law gives retail security officers fewer powers than most people assume — but used correctly by trained professionals, those powers are enough. Here is where the lines actually sit.",
    ],
    sections: [
      {
        h: "The starting point: citizen's powers",
        paras: [
          "A security guard in England has no police powers — no power to search without consent, no power to demand identification, no power of arrest beyond what any citizen holds. Their authority comes from the same place as any employee's: the occupier's right to set conditions of entry and protect property.",
          "What professional training adds is knowing exactly how far those rights extend — and how to work within them so that evidence stands up and claims do not.",
        ],
      },
      {
        h: "Searches: consent is everything",
        paras: [
          "Bag searches in retail are consensual conditions of entry. A guard can ask to look inside your bag; you can refuse; the store can then ask you to leave. What nobody may do is search you by force — a non-consensual search is assault, whoever authorises it.",
          "Well-run stores make the condition visible at entry and train officers to ask consistently and courteously, because selective or aggressive searching creates discrimination claims faster than it catches thieves.",
        ],
      },
      {
        h: "Detention and the citizen's arrest",
        paras: [
          "Any person in England may arrest someone committing an indictable offence — including theft — where it is not reasonably practicable for a police officer to make the arrest instead. That is the legal basis for detaining a shoplifter caught in the act.",
          "The limits are strict: reasonable force only, detention no longer than necessary, and police called immediately. Professional officers are trained to observe, contain and hand over — the era of the back-room interrogation is over, and rightly.",
        ],
      },
      {
        h: "Why trained officers protect the retailer",
        paras: [
          "Every wrongful accusation, forced search or excessive restraint lands on the retailer's reputation and insurance. SIA-licensed, properly trained officers build evidence packs instead: observed selection, concealment, non-payment, continuous observation — the chain that supports prosecution or a banning order.",
          "Xtreme Protection provides retail security guards and store detectives across England, trained in lawful, evidence-led loss prevention. Call +44 2046 402840.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can a security guard physically stop a shoplifter?",
        a: "Yes, within limits: reasonable force may be used to detain someone caught committing theft, pending police arrival. Force must be proportionate, and professional officers are trained to de-escalate and contain rather than pursue confrontation.",
      },
      {
        q: "Can a store ban a customer permanently?",
        a: "Yes — private retailers may refuse entry to anyone (subject to equality law), and many operate banning schemes for repeat offenders. Guards enforce the ban as a condition of entry, with incidents documented.",
      },
      {
        q: "Can security guards demand to see ID?",
        a: "They can ask; you can decline. There is no general power to demand identification, though a citizen's arrest situation changes the practical position once police are called.",
      },
    ],
    relatedServices: ["retail-security", "manned-guarding", "cctv-monitoring"],
    relatedCities: ["london", "birmingham", "manchester"],
  },
  {
    slug: "construction-cctv-towers-guide",
    title: "CCTV Towers for Construction Sites: A Buyer's Guide",
    description: "Rapid-deployment CCTV towers have changed construction site security. Power options, monitoring, audio challenge and how towers compare to guards.",
    category: "Sector Guides",
    date: "2026-07-22",
    readMins: 6,
    intro: [
      "A decade ago, site security meant a guard in a cabin or nothing. Then rapid-deployment CCTV towers arrived — self-powered, monitored around the clock, and installable in a day — and the economics of protecting a construction site changed completely. Here is what to know before you hire one.",
    ],
    sections: [
      {
        h: "What a rapid-deployment tower actually is",
        paras: [
          "A tower is a telescopic mast — typically six to eight metres — carrying PTZ cameras with detection analytics, infrared night vision, loudspeakers for live audio challenge, and its own power and connectivity. It arrives on a trailer, is positioned and commissioned within hours, and needs nothing from your site.",
          "Detection zones are drawn around your perimeter and assets; when they are breached, the system wakes an operator in a monitoring centre rather than just recording footage nobody watches until Monday.",
        ],
      },
      {
        h: "Power, connectivity and placement",
        paras: [
          "Modern towers run on battery with solar or hydrogen fuel cell backup — no mains, no generator, no fuel bills. Connectivity is over 4G/5G mobile networks, so even greenfield land with no infrastructure is covered from day one.",
          "Placement is the craft: covering gates and haul routes first, then materials compounds and plant parking, with sightlines clear of the cabins and scaffolding that will appear as the site grows. Good providers survey and reposition as your phases change.",
        ],
      },
      {
        h: "Monitoring and the audio challenge",
        paras: [
          "The live audio warning is the tower's killer feature: the moment an intruder is detected, an operator speaks directly to the site — you are being watched, leave now, response is on its way. The vast majority of intrusions end at that sentence, before damage or theft.",
          "Where the warning is ignored, verified footage dispatches a response officer or police with real-time description and direction of travel — evidence-led response rather than another false alarm.",
        ],
      },
      {
        h: "Towers versus guards — and the insurance angle",
        paras: [
          "Towers cost a fraction of 24/7 guarding and never blink; guards intervene physically and manage the gate. Most larger sites run both: towers for the perimeter night and weekend blanket, officers for access control and high-risk phases.",
          "Check your insurance wording: many construction policies now specify monitored detection for unattended sites, and a compliant tower system often satisfies conditions that recording-only CCTV does not.",
          "Xtreme Protection supplies monitored CCTV towers across England, usually installed within days of survey. Call +44 2046 402840.",
        ],
      },
    ],
    faqs: [
      {
        q: "How quickly can a CCTV tower be installed?",
        a: "Typically within two to three days of the site survey — same week for urgent requirements. The unit arrives self-contained and is commissioned within hours of positioning.",
      },
      {
        q: "Do towers need power or internet on site?",
        a: "No. Rapid-deployment towers are self-powered (battery with solar or fuel-cell backup) and connect over mobile networks, making them ideal for sites with no utilities yet.",
      },
      {
        q: "What happens when a tower is vandalised or tampered with?",
        a: "Towers have anti-tamper detection that alarms the monitoring centre immediately on approach, tilt or cover — often catching the attempt live on camera with an audio warning before damage is done.",
      },
    ],
    relatedServices: ["construction-site-security", "cctv-monitoring", "k9-security-dog-handlers"],
    relatedCities: ["milton-keynes", "peterborough", "northampton"],
  },
  {
    slug: "vacant-property-insurance-requirements",
    title: "Vacant Property Insurance: The Security Conditions That Decide Your Claim",
    description: "When a building goes empty, your insurance quietly changes. The inspection and security conditions in most policies — and how to stay covered.",
    category: "Compliance",
    date: "2026-07-22",
    readMins: 6,
    intro: [
      "Somewhere in your property insurance schedule is a paragraph most landlords never read until a claim is refused: the unoccupancy conditions. The moment a building becomes vacant, cover typically restricts and duties begin — and meeting them is entirely on you.",
    ],
    sections: [
      {
        h: "How cover changes when a building empties",
        paras: [
          "Standard policies assume occupation. After a continuous empty period — often 30 to 45 days — insurers commonly restrict perils (escape of water, malicious damage and theft may be excluded or excesses multiplied) and impose conditions precedent to cover.",
          "The logic is actuarial: empty buildings suffer more losses and worse losses, because nobody is there to notice a leak at hour one or a squatter at week one.",
        ],
      },
      {
        h: "The conditions you will usually find",
        paras: [
          "The typical list reads: regular documented inspections (weekly is common), premises secured against unauthorised entry, letterboxes sealed, utilities isolated or managed, combustible waste removed, and the insurer notified of the vacancy.",
          "Some policies go further for higher-risk voids: boarding to openings, monitored alarm or CCTV, and evidence that inspections actually happened — not just were scheduled.",
        ],
      },
      {
        h: "Evidence: the inspection report is the product",
        paras: [
          "In a disputed claim, the question is never whether you cared about the building — it is whether you can prove the conditions were met. A dated, time-stamped, photographed inspection report for every required visit is the difference between a paid claim and a lawsuit.",
          "Professional vacant property inspection services exist precisely for this: a licensed contractor attending on the required schedule, checking a defined list, and producing insurer-grade documentation automatically.",
        ],
      },
      {
        h: "Getting compliant fast",
        paras: [
          "If a building is about to empty: notify your insurer or broker, get the unoccupancy conditions in writing, then line up the security and inspection regime before the last tenant leaves — not after. Physical security, monitored towers and inspections can all be in place within days.",
          "Xtreme Protection provides vacant property inspections, patrols, monitored CCTV and physical security across England, with insurer-friendly reporting as standard. Call +44 2046 402840.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often must a vacant property be inspected?",
        a: "Weekly is the most common condition, but policies vary — some require twice-weekly or daily checks for high-risk buildings. Always confirm your policy's exact wording rather than assuming.",
      },
      {
        q: "Can I do the inspections myself?",
        a: "Some insurers accept owner inspections; others require an independent or professional contractor. Even where DIY is allowed, professional reports carry more weight in a disputed claim because they are systematic and timestamped.",
      },
      {
        q: "What happens if I breach the conditions?",
        a: "Breach of a condition precedent can void cover for related claims entirely — a fire or theft with no valid inspection regime can leave the full loss with you. Compliance paperwork is cheap insurance for your insurance.",
      },
    ],
    relatedServices: ["vacant-property-security", "cctv-monitoring", "mobile-security-patrols"],
    relatedCities: ["liverpool", "sunderland", "blackburn"],
  },
  {
    slug: "concierge-security-residential",
    title: "Concierge Security for Residential Blocks: More Than a Friendly Face",
    description: "Residential concierge security blends hospitality with vigilance. What concierge officers do daily and why developments across England are switching.",
    category: "Sector Guides",
    date: "2026-07-23",
    readMins: 5,
    intro: [
      "The best residential concierge is the one residents describe as helpful, and the managing agent describes as essential. Concierge security has quietly become the standard for premium blocks because it solves two problems at once: the building needs a host, and the building needs a guard.",
    ],
    sections: [
      {
        h: "The daily reality of the role",
        paras: [
          "A concierge officer's day runs through the building's life: accepting and logging the parcel avalanche, managing keys and access for contractors, signing in cleaners and carers, monitoring CCTV and access systems, and walking the building — corridors, plant rooms, car parks, bin stores — on scheduled patrols.",
          "Around the routine sits the incident load: water leaks found on patrol, alarms investigated, noise complaints handled, and the occasional 2am situation that needs calm authority rather than a phone call to a managing agent's voicemail.",
        ],
      },
      {
        h: "Why the hybrid beats both alternatives",
        paras: [
          "A pure concierge without security training cannot lawfully or safely handle the enforcement side; a pure security guard without service instincts makes a residential building feel like a facility. The hybrid officer — SIA licensed, vetted, and hospitality-presented — carries both.",
          "For residents the result is a building that feels cared for; for the agent or freeholder, it is an audit trail of patrols, incidents, contractor visits and access events, reported monthly.",
        ],
      },
      {
        h: "The antisocial behaviour bridge",
        paras: [
          "Every block manager knows the slow-burn issues: the sublet party flat, the contested parking space, the resident whose visitors concern the neighbours. A consistent concierge presence documents patterns professionally, enforces house rules evenly, and provides the contemporaneous records that formal action later depends on.",
          "Because officers are a daily, trusted presence, they also hear things early — the intelligence that lets management act before escalation.",
        ],
      },
      {
        h: "Structuring cover for your development",
        paras: [
          "Patterns flex to the building: 24/7 concierge for large or premium developments, day-and-evening cover with overnight mobile patrol and response for smaller ones, and portfolio models where several nearby blocks share patrol and response resources.",
          "Xtreme Protection provides concierge security to residential developments, build-to-rent schemes and mixed-use blocks across England. Call +44 2046 402840.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the difference between a concierge and a concierge security officer?",
        a: "A concierge provides service; a concierge security officer provides service plus licensed security capability — access enforcement, incident response, patrols and documented reporting. For residential blocks, the hybrid covers both the welcome and the risk.",
      },
      {
        q: "Do concierge officers patrol, or stay at the desk?",
        a: "Both, on a written schedule: desk and service duties alongside timed patrols of communal areas, plant rooms, car parks and perimeter, with every patrol point verified and logged.",
      },
      {
        q: "Can smaller blocks afford concierge security?",
        a: "Full-time concierge suits larger developments; smaller blocks typically use part-week concierge presence combined with mobile patrols and keyholding response — much of the benefit at a shared cost.",
      },
    ],
    relatedServices: ["concierge-security", "corporate-security", "key-holding-alarm-response"],
    relatedCities: ["london", "manchester", "birmingham"],
  },
  {
    slug: "close-protection-who-needs-it",
    title: "Close Protection in England: Who Actually Needs a Bodyguard?",
    description: "Close protection is not just for celebrities. Who uses personal protection, what a CP officer actually does, and how engagements are planned.",
    category: "Guides",
    date: "2026-07-23",
    readMins: 6,
    intro: [
      "Mention close protection and most people picture sunglasses and an earpiece trailing a celebrity. The reality in England is quieter and broader: executives facing disgruntled-employee threats, witnesses in difficult trials, high-net-worth families during public disputes — ordinary risk, professionally managed.",
    ],
    sections: [
      {
        h: "Who actually uses close protection",
        paras: [
          "The client list surprises people: corporate leaders through restructures and hostile negotiations; legal parties and witnesses in contentious cases; lottery winners and sudden wealth; public figures at book signings and court appearances; families managing a specific, time-bound threat.",
          "Most engagements are short and specific — a court week, a public appearance, a property transaction under dispute — rather than permanent details.",
        ],
      },
      {
        h: "Threat assessment before sunglasses",
        paras: [
          "Professional close protection starts with analysis, not presence: what is the threat, who carries it, where does the client's routine expose them, and what changes shrink the risk before anyone is hired to stand anywhere. Often the answer includes journey planning and residential security rather than a visible escort.",
          "This planning phase is what separates licensed professionals from rented muscle — and it is what insurers and courts expect to see documented if an incident ever occurs.",
        ],
      },
      {
        h: "What a CP officer does day to day",
        paras: [
          "Advance work dominates: routes checked, venues recce'd, arrivals and departures timed to avoid exposure. With the principal, the officer manages space and exits rather than looking for confrontation — the skill is making problems not happen, invisibly.",
          "In England, close protection officers are unarmed as a matter of law; their tools are planning, positioning, awareness and communication, all under an SIA close protection licence.",
        ],
      },
      {
        h: "Discretion as the deliverable",
        paras: [
          "The best close protection is the kind colleagues barely register: the officer reads as a colleague or aide, the client's schedule runs normally, and life continues without the theatre. Embarrassment is a cost too, and good teams minimise it.",
          "Xtreme Protection provides licensed close protection and executive security across England. For a confidential discussion, call +44 2046 402840 or email admin@xtremeprotection.uk.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need one officer or a team?",
        a: "It follows the threat assessment: many engagements need only a single officer for specific movements or events, while elevated or complex risk may justify a small team with advance and residential cover.",
      },
      {
        q: "Are close protection officers armed?",
        a: "No — private security in England is unarmed, without exception for close protection. Protection is delivered through planning, positioning and response, not firearms.",
      },
      {
        q: "How discreet can protection be?",
        a: "Very. Plain-clothes officers can read as staff, drivers or colleagues, and much of the work — advance planning, route management, venue liaison — happens before the client is ever present.",
      },
    ],
    relatedServices: ["close-protection", "corporate-security", "hotel-security"],
    relatedCities: ["london", "manchester", "leeds"],
  },
  {
    slug: "night-time-security-businesses",
    title: "Night-Time Security: Why Most Business Crime Happens After Dark",
    description: "Empty premises between 6pm and 6am carry the majority of commercial break-in risk. The night-time security layers that actually work for businesses.",
    category: "Guides",
    date: "2026-07-24",
    readMins: 6,
    intro: [
      "Between six in the evening and six in the morning, your business is a different risk: unlit, unstaffed and unobserved. The majority of commercial burglaries target exactly this window — which is why the hours you are closed deserve more security thought than the hours you are open.",
    ],
    sections: [
      {
        h: "The night risk window",
        paras: [
          "Darkness compresses the intruder's risk: fewer witnesses, slower response, hours of working time before the morning discovery. Weekends stretch the window further — a Friday night break-in can run undisturbed until Monday.",
          "Different premises face different night threats: retail and warehouses face organised theft; offices face equipment and data theft; construction and voids face metal and plant crime; licensed venues face the aftermath of their own evening.",
        ],
      },
      {
        h: "The layered night answer",
        paras: [
          "Night security layers up rather than relying on one measure: physical security and lighting as the foundation; monitored CCTV for continuous detection; mobile patrols for unpredictable human presence; key holding and alarm response so every activation gets a professional; and static guarding for sites whose risk justifies a constant presence.",
          "The mix follows the risk assessment, not habit — a quiet office and a cash-handling depot need entirely different nights.",
        ],
      },
      {
        h: "The cheap wins before the spend",
        paras: [
          "Before any contract: lighting that removes shadow, sightlines cleared of cover, locks and glazing that match the risk, stock and cash out of view, and an alarm that actually gets answered. These change the criminal's maths before a single officer is hired.",
          "Staff routines matter too: who locks up, what is checked, what is left visible overnight — the last hour of the working day sets up the whole night.",
        ],
      },
      {
        h: "Making nights boring",
        paras: [
          "The goal of night security is a permanent lack of news: no incidents, full reports, verified patrols. When your morning briefing shows checks completed and nothing found, the system is working exactly as designed.",
          "Xtreme Protection designs night-time security packages across England — patrols, monitoring, response and guarding. Call +44 2046 402840 for a free night-risk assessment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are nights really riskier than the daytime?",
        a: "For most commercial premises, yes — the combination of darkness, emptiness and delayed discovery makes overnight the preferred window for break-ins, which is why insurance conditions often target the unoccupied hours specifically.",
      },
      {
        q: "Is a night guard or patrols better value?",
        a: "A static night guard offers continuous presence for high-risk sites; patrols offer verified visits several times a night at a fraction of the cost for lower-risk ones. Many sites blend: guarding on peak-risk nights, patrols otherwise.",
      },
      {
        q: "What about weekends and bank holidays?",
        a: "They are the highest-risk periods because the empty window is longest. Cover patterns should increase on weekends and holidays — a schedule designed for Tuesday night will not survive a bank holiday weekend.",
      },
    ],
    relatedServices: ["mobile-security-patrols", "manned-guarding", "key-holding-alarm-response"],
    relatedCities: ["blackpool", "portsmouth", "southampton"],
  },
  {
    slug: "sia-approved-contractor-scheme",
    title: "The SIA Approved Contractor Scheme: What ACS Actually Tells a Buyer",
    description: "ACS accreditation is the security industry's quality kite-mark for companies. What the scheme audits, what it does not guarantee, and how to check a provider.",
    category: "Compliance",
    date: "2026-07-24",
    readMins: 5,
    intro: [
      "Security companies love badges, and the SIA Approved Contractor Scheme is the one that matters most. But buyers regularly confuse what ACS actually certifies — so here is what the scheme audits, what it cannot tell you, and how to use it when choosing a provider.",
    ],
    sections: [
      {
        h: "What ACS is",
        paras: [
          "The Approved Contractor Scheme is the Security Industry Authority's voluntary accreditation for security companies — as distinct from individual licensing, which is mandatory. Companies are independently assessed against a demanding set of operational and quality standards.",
          "Assessment covers the machinery of a security company: leadership and strategy, financial probity, staff vetting and training, supervision, health and safety, quality management and customer service processes.",
        ],
      },
      {
        h: "What the assessment actually examines",
        paras: [
          "ACS auditors look at how a company runs, not how its website reads: vetting files against BS 7858, training records, licence checking processes, contract management, incident handling, and how performance is measured and reviewed with clients.",
          "In other words, ACS tests exactly the systems that determine whether your Tuesday night shift happens properly — which is why procurement teams and public bodies so often require it.",
        ],
      },
      {
        h: "What ACS does not guarantee",
        paras: [
          "Accreditation certifies systems at assessment time; it is not a promise that every shift will be perfect, and it is not the only mark of a good provider. Some excellent companies choose not to join; some accredited companies still disappoint.",
          "Use ACS as a strong positive signal, then do the buyer's checks anyway: verification technology, sample reports, named contract management and references from contracts like yours.",
        ],
      },
      {
        h: "Checking a provider's status",
        paras: [
          "The SIA publishes the register of approved contractors — check the company name there rather than trusting a logo. Status, score banding and any conditions are visible, and absence from the register answers its own question.",
          "Xtreme Protection operates to ACS-aligned standards across England: licensed officers, BS 7858 vetting, verified delivery and monthly client reviews. Call +44 2046 402840 and ask us the awkward questions — we would rather you did.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is ACS accreditation mandatory for security companies?",
        a: "No — it is voluntary. Individual officer licensing is the legal requirement; ACS is a quality accreditation for companies on top of that. Its voluntary nature is part of its value as a signal.",
      },
      {
        q: "Does using an ACS company guarantee good service?",
        a: "It guarantees the company has been independently assessed against strong operational standards, which removes much of the buyer's risk — but you should still verify delivery systems, reporting and references for your own contract.",
      },
      {
        q: "How do I check whether a company is ACS approved?",
        a: "Search the SIA's published register of approved contractors on the SIA website. Never rely on a logo on a website or van — the register is the only authoritative source.",
      },
    ],
    relatedServices: ["manned-guarding", "corporate-security", "door-supervision"],
    relatedCities: ["london", "birmingham", "leeds"],
  },
];

export default ARTICLES_3;
